import { JSX } from 'react';
import sanitizeHtml from 'sanitize-html';

import { cn, Text } from '@videoblocks/design-system';
import { generateSchema, SCHEMA_TYPES } from '@videoblocks/structured-data';

import LinkWrapper from '../elements/LinkWrapper';
import ProgressiveImage from '../elements/ProgressiveImage/ProgressiveImage';
import StructuredData from '../elements/StructuredData';
import BuilderSection from '../layouts/BuilderSection';

export interface FeatureCards {
    image: string;
    imageAlt: string;
    title: string;
    body?: string;
    optionalText?: string;
    link?: string;
    price?: string;
    currency?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'LimitedAvailability';
}

export interface FeaturesCardsProps {
    heading: string;
    useBodyText?: boolean;
    bodyText: string;
    featureCards: FeatureCards[];
    backgroundColor?: string;
    cardBackgroundColor?: string;
    cardPadding?: string;
    cardBorderRadius?: string;
    enableMobileSwipe?: boolean;
    textColor?: string;
    cardTextColor?: string;
    optionalTextOpacity?: number;
    structuredDataType?: 'product' | 'service' | 'article' | 'creative-work' | 'none';
    structuredDataUrl?: string;
    organizationName?: string;
}

export default function FeaturesCards({
    heading,
    useBodyText = false,
    bodyText,
    featureCards,
    backgroundColor,
    cardBackgroundColor,
    cardPadding,
    cardBorderRadius,
    enableMobileSwipe = false,
    textColor,
    cardTextColor,
    optionalTextOpacity = 0.8,
    structuredDataType = 'none',
    structuredDataUrl,
    organizationName = 'Storyblocks',
}: FeaturesCardsProps): JSX.Element {
    const containerClasses = enableMobileSwipe
        ? 'flex gap-8 overflow-x-auto pb-4 md:overflow-visible flex-nowrap md:flex-wrap md:justify-center'
        : 'flex gap-8 flex-wrap justify-center';

    const getCardSizes = () => {
        const cardCount = featureCards?.length || 0;
        const baseClasses = 'grow shrink-0 w-full max-w-md';

        if (cardCount === 2) {
            // 2 cards: 1 per row on mobile, 2 per row on md+
            return `${baseClasses} md:w-[calc(50%-16px)]`;
        } else if (cardCount === 3) {
            // 3 cards: 1 per row on mobile, 2 per row on md, 3 per row on lg+
            return `${baseClasses} md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]`;
        } else {
            // 4+ cards: Full responsive grid (1→2→3→4 per row)
            return `${baseClasses} md:w-[calc(50%-16px)] lg:w-[calc(25%-24px)]`;
        }
    };

    const getImageBorderRadius = () => {
        const defaultRadius = cardBorderRadius || 'var(--radius-lg, 1rem)';

        if (cardBackgroundColor && backgroundColor && cardBackgroundColor !== backgroundColor) {
            return `${defaultRadius} ${defaultRadius} 0 0`;
        }

        return defaultRadius;
    };

    const structuredData =
        structuredDataType === 'none' || !featureCards?.length
            ? null
            : generateSchema(SCHEMA_TYPES.ITEM_LIST, {
                  schemaType: structuredDataType,
                  heading,
                  description: useBodyText ? bodyText : undefined,
                  items: featureCards.map((card) => ({
                      title: card.title,
                      description: card.body,
                      image: card.image,
                      url: card.link,
                      price: card.price,
                      currency: card.currency,
                      availability: card.availability,
                  })),
                  url: structuredDataUrl,
                  organizationName,
              });

    return (
        <div data-testid="features-cards-container" style={{ backgroundColor }}>
            {structuredData && <StructuredData data={structuredData} />}
            <BuilderSection>
                <div data-testid="features-cards-content" className="flex flex-col w-full">
                    <div
                        className={cn(
                            'flex flex-col items-center justify-center text-center gap-4',
                            bodyText ? 'mb-8 md:mb-12' : 'mb-8',
                        )}
                    >
                        <Text asChild fontFamily="polysans" fontSize="4xl" className="font-bold">
                            <h2
                                data-testid="features-cards-heading"
                                className="m-0"
                                style={{ color: textColor || '#111827' }}
                            >
                                {heading}
                            </h2>
                        </Text>

                        {useBodyText && (
                            <Text
                                asChild
                                fontSize="xl"
                                fontWeight="regular"
                                fontFamily="polysans"
                                className="max-w-2xl mx-auto"
                            >
                                <div
                                    data-testid="features-cards-body-text"
                                    style={{ color: textColor || '#111827' }}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyText) }}
                                />
                            </Text>
                        )}
                    </div>

                    <div data-testid="features-cards-grid" className={containerClasses}>
                        {featureCards &&
                            featureCards.length > 0 &&
                            featureCards.map((card, index) => (
                                <LinkWrapper
                                    key={`feature-card-${index}`}
                                    link={card.link}
                                    className={cn('flex flex-col items-center', getCardSizes())}
                                    style={{
                                        backgroundColor: cardBackgroundColor,
                                        borderRadius: cardBorderRadius || 'var(--radius-lg, 1rem)',
                                    }}
                                    data-testid={`feature-card-${index}`}
                                    tabIndex={card.link ? 0 : -1}
                                    role={card.link ? 'button' : undefined}
                                    aria-label={card.link ? `Navigate to ${card.title}` : undefined}
                                >
                                    {card.image && (
                                        <div
                                            data-testid={`feature-card-image-container-${index}`}
                                            className="w-full aspect-square overflow-hidden bg-white my-0 mb-6"
                                            style={{
                                                borderRadius: getImageBorderRadius(),
                                            }}
                                        >
                                            <ProgressiveImage
                                                src={card.image}
                                                alt={card.imageAlt}
                                                className="w-full h-full object-cover"
                                                width="288"
                                                height="288"
                                                shouldLazyLoad
                                                containerClassName="w-full h-full"
                                            />
                                        </div>
                                    )}
                                    <div
                                        className="flex flex-col gap-2"
                                        style={{
                                            padding: cardPadding,
                                        }}
                                    >
                                        {card.optionalText && (
                                            <p
                                                data-testid={`feature-card-optional-text-${index}`}
                                                className="my-0 font-medium text-xs"
                                                style={{
                                                    color: cardTextColor || '#111827',
                                                    opacity: optionalTextOpacity,
                                                }}
                                            >
                                                {card.optionalText}
                                            </p>
                                        )}
                                        <Text asChild fontSize="2xl" fontFamily="polysans" className="text-2xl">
                                            <h3
                                                data-testid={`feature-card-title-${index}`}
                                                className="mt-0 font-bold"
                                                style={{ color: cardTextColor || '#111827' }}
                                            >
                                                {card.title}
                                            </h3>
                                        </Text>

                                        {!!card.body?.trim() && (
                                            <Text
                                                asChild
                                                fontSize="lg"
                                                fontWeight="regular"
                                                fontFamily="polysans"
                                                className="text-lg"
                                            >
                                                <div
                                                    data-testid={`feature-card-body-${index}`}
                                                    style={{ color: cardTextColor || '#111827' }}
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.body) }}
                                                />
                                            </Text>
                                        )}
                                    </div>
                                </LinkWrapper>
                            ))}
                    </div>
                </div>
            </BuilderSection>
        </div>
    );
}
